import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/apiService';
import { useAuth } from './AuthContext';
import API_BASE_URL from '../config/api';

const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

// Auto-refresh interval in milliseconds (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

export const AdminProvider = ({ children }) => {
    const { user } = useAuth();

    // --- REAL STATE ---

    // Users
    const [users, setUsers] = useState([]);

    // Franchises
    const [franchises, setFranchises] = useState([]);

    // Approvals (KYC & Withdrawal)
    const [kycRequests, setKycRequests] = useState([]);
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);

    // Settings
    const [settings, setSettings] = useState({
        companyName: 'SwiftMLM Network',
        supportEmail: 'support@swiftlink.swift2ai.com',
        currency: 'INR',
        timezone: 'IST',
        adminFee: 5,
        tds: 10,
        minWithdrawal: 500,
        twoFactor: false,
        registrationOpen: true
    });

    // --- AUTO-REFRESH STATE ---
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);

    // Fetch Initial Data
    const fetchUsers = useCallback(async (silent = false, filters = {}) => {
        try {
            if (user?.role === 'admin') {
                if (!silent) setIsRefreshing(true);
                const response = await apiService.getAdminUsers(filters);
                if (response.success && isMountedRef.current) {
                    setUsers(response.data);
                }
            }
        } catch (error) {
            console.error("Failed to fetch admin users", error);
        } finally {
            if (!silent && isMountedRef.current) setIsRefreshing(false);
        }
    }, [user]);

    const fetchFranchises = useCallback(async (silent = false) => {
        try {
            if (user?.role === 'admin') {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/admin/franchises.php`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success && isMountedRef.current) {
                    // Transform backend data to match frontend format
                    const formattedFranchises = data.data.map(f => ({
                        id: f.id,
                        name: f.name || 'Unnamed Franchise',
                        owner: f.owner || 'Not Available',
                        region: f.region || 'Location Not Set',
                        stock: f.stock || 0,
                        sales: f.sales || 0,
                        status: f.status || 'active',
                        type: f.type || 'Standard',
                        role: f.role || 'franchise'
                    }));
                    setFranchises(formattedFranchises);
                }
            }
        } catch (error) {
            console.error("Failed to fetch franchises", error);
        }
    }, [user]);

    const fetchApprovals = useCallback(async (silent = false) => {
        try {
            if (user?.role === 'admin') {
                const token = localStorage.getItem('authToken');

                // Fetch KYC requests
                try {
                    const kycResponse = await fetch(`${API_BASE_URL}/admin/kyc_requests.php?_t=${Date.now()}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    });
                    const kycText = await kycResponse.text();

                    try {
                        const kycData = JSON.parse(kycText);
                        if (kycData.success && isMountedRef.current) {
                            setKycRequests(kycData.data || []);
                        }
                    } catch (parseError) {
                        console.error("KYC JSON Parse Error:", parseError);
                        console.error("KYC Response Text:", kycText);
                    }
                } catch (kycError) {
                    console.error("Failed to fetch KYC requests:", kycError);
                }

                // Fetch Withdrawal requests
                try {
                    const withdrawalResponse = await fetch(`${API_BASE_URL}/admin/withdrawal_requests.php?_t=${Date.now()}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    });
                    const withdrawalText = await withdrawalResponse.text();

                    try {
                        const withdrawalData = JSON.parse(withdrawalText);
                        if (withdrawalData.success && isMountedRef.current) {
                            setWithdrawalRequests(withdrawalData.data || []);
                        }
                    } catch (parseError) {
                        console.error("Withdrawal JSON Parse Error:", parseError);
                        console.error("Withdrawal Response Text:", withdrawalText);
                    }
                } catch (withdrawalError) {
                    console.error("Failed to fetch withdrawal requests:", withdrawalError);
                }
            }
        } catch (error) {
            console.error("Failed to fetch approvals", error);
        }
    }, [user]);

    // Refresh all data
    const refreshAllData = useCallback(async (silent = false) => {
        if (!silent) setIsRefreshing(true);
        try {
            await Promise.all([
                fetchUsers(true),
                fetchFranchises(true),
                fetchApprovals(true)
            ]);
            if (isMountedRef.current) {
                setLastUpdated(new Date());
            }
        } finally {
            if (isMountedRef.current) setIsRefreshing(false);
        }
    }, [fetchUsers, fetchFranchises, fetchApprovals]);

    // Toggle auto-refresh
    const toggleAutoRefresh = useCallback(() => {
        setAutoRefreshEnabled(prev => !prev);
    }, []);

    // Initial fetch
    useEffect(() => {
        isMountedRef.current = true;

        // Call fetchers only if admin
        if (user?.role === 'admin') {
            refreshAllData(false);
        }

        return () => {
            isMountedRef.current = false;
        };
    }, [user, refreshAllData]);

    // Auto-refresh interval
    useEffect(() => {
        if (user?.role === 'admin' && autoRefreshEnabled) {
            intervalRef.current = setInterval(() => {
                refreshAllData(true); // Silent refresh
            }, AUTO_REFRESH_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [user, autoRefreshEnabled, refreshAllData]);


    // --- ACTIONS ---

    // User Actions
    const blockUser = async (userId, reason = 'Admin Action') => {
        try {
            const response = await apiService.adminManageUser('block', userId, { reason });
            if (response.success) {
                setUsers(users.map(u => u.id === userId ? { ...u, status: 'Blocked', blockReason: reason } : u));
                fetchUsers(); // Sync with DB
            }
        } catch (error) {
            console.error("Failed to block user", error);
        }
    };

    const unblockUser = async (userId) => {
        try {
            const response = await apiService.adminManageUser('active', userId);
            if (response.success) {
                setUsers(users.map(u => u.id === userId ? { ...u, status: 'Active', blockReason: null } : u));
                fetchUsers(); // Sync with DB
            }
        } catch (error) {
            console.error("Failed to unblock user", error);
        }
    };

    const deleteUser = async (userId) => {
        try {
            const response = await apiService.adminManageUser('delete', userId);
            if (response.success) {
                setUsers(users.filter(u => u.id !== userId));
                fetchUsers(true); // Re-sync with DB
            }
        } catch (error) {
            console.error("Failed to delete user", error);
        }
    };

    const addUser = async (newUser) => {
        try {
            const response = await apiService.register(newUser); // Assuming register endpoint works for admins too, otherwise need specific admin add user endpoint
            if (response.success) {
                // Refresh list or add result
                const addedUser = { ...newUser, id: response.data?.id || (users.length + 100), joinDate: new Date().toISOString().split('T')[0], status: 'Active', rank: 'None', avatar: newUser.name.charAt(0) };
                setUsers([addedUser, ...users]); // Add to top
            }
        } catch (error) {
            console.error("Failed to add user", error);
        }
    };

    const updateUser = async (id, updatedData) => {
        try {
            const response = await apiService.adminManageUser('update_user', id, updatedData);
            if (response.success) {
                setUsers(users.map(u => u.id === id ? { ...u, ...updatedData } : u));
                fetchUsers(true); // Re-sync with DB
            }
            return response;
        } catch (error) {
            console.error("Failed to update user", error);
            throw error;
        }
    };

    // Franchise Actions
    const addFranchise = async (newFranchise) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/franchises.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: newFranchise.franchiseName,
                    email: newFranchise.email,
                    location: newFranchise.address,
                    city: newFranchise.city || newFranchise.region,
                    state: newFranchise.state || '',
                    type: newFranchise.type || 'mini'
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchFranchises(); // Refresh from DB
                return true;
            }
            return false;
        } catch (error) {
            console.error("Failed to add franchise", error);
            return false;
        }
    };

    const updateFranchise = (id, updatedData) => {
        setFranchises(franchises.map(f => f.id === id ? { ...f, ...updatedData } : f));
    };

    const deleteFranchise = (id) => {
        setFranchises(franchises.filter(f => f.id !== id));
    };

    // Approval Actions
    const approveKyc = async (id) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/kyc_requests.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'approve', kyc_id: id })
            });
            const data = await response.json();
            if (data.success) {
                setKycRequests(kycRequests.filter(req => req.id !== id));
                fetchApprovals(); // Refresh
            }
        } catch (error) {
            console.error("Failed to approve KYC", error);
        }
    };

    const rejectKyc = async (id) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/kyc_requests.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'reject', kyc_id: id })
            });
            const data = await response.json();
            if (data.success) {
                setKycRequests(kycRequests.filter(req => req.id !== id));
                fetchApprovals(); // Refresh
            }
        } catch (error) {
            console.error("Failed to reject KYC", error);
        }
    };

    const approveWithdrawal = async (id) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/withdrawal_requests.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'approve', withdrawal_id: id })
            });
            const data = await response.json();
            if (data.success) {
                setWithdrawalRequests(withdrawalRequests.filter(req => req.id !== id));
                fetchApprovals(); // Refresh
            }
        } catch (error) {
            console.error("Failed to approve withdrawal", error);
        }
    };

    const rejectWithdrawal = async (id) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/withdrawal_requests.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'reject', withdrawal_id: id })
            });
            const data = await response.json();
            if (data.success) {
                setWithdrawalRequests(withdrawalRequests.filter(req => req.id !== id));
                fetchApprovals(); // Refresh
            }
        } catch (error) {
            console.error("Failed to reject withdrawal", error);
        }
    };

    // Settings Actions
    const updateSettings = (newSettings) => {
        setSettings({ ...settings, ...newSettings });
    };

    const value = {
        users,
        franchises,
        kycRequests,
        withdrawalRequests,
        settings,
        blockUser,
        unblockUser,
        deleteUser,
        addUser,
        updateUser,
        addFranchise,
        updateFranchise,
        deleteFranchise,
        approveKyc,
        rejectKyc,
        approveWithdrawal,
        rejectWithdrawal,
        updateSettings,
        fetchUsers,
        fetchFranchises,
        fetchApprovals,
        // Auto-refresh properties
        autoRefreshEnabled,
        toggleAutoRefresh,
        lastUpdated,
        isRefreshing,
        refreshAllData
    };

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};


